import css from './CareerFeaturesView.module.scss';
import type { SlideType } from '@components/SplitViewSlides/SplitViewSlides';
import React, { FC, useMemo, useState, useEffect } from 'react';
import { findLastIndex, normalize } from '@utils';
import Image from 'next/image';

export interface CareerFeaturesViewProps {
	seek: number;
	slides: Array<SlideType>;
	target?: number;
}

export const CareerFeaturesView: FC<CareerFeaturesViewProps> = React.memo((props) => {
	const { seek, target, slides } = props;

	const stages = useMemo(
		() =>
			slides.map((_, i) =>
				normalize(seek, (i + 1) * (1 / (slides.length - 1)), i * (1 / (slides.length - 1)), true)
			),
		[seek, slides]
	);

	const [canvas, setCanvas] = useState<HTMLDivElement | null>();
	const [track, setTrack] = useState<HTMLDivElement | null>();

	const frag = 1 / slides.length;
	const current = target
		? Math.round((Number((target + frag).toFixed(4)) * slides.length - 1) * 1000) / 1000
		: Math.min(
				findLastIndex(stages, (value) => value >= 1),
				stages.length - 2
		  ) + 1;

	useEffect(() => {
		if (canvas) {
			canvas.style.cssText = `
				--sq-scale: ${1 - stages[0]};
				--sq-opacity: ${1 - stages[0]};
			`;
		}
	}, [stages, canvas]);

	useEffect(() => {
		if (track) {
			track.style.cssText = `--translateY: ${-(100 / slides.length) * current}%;`;
		}
		track?.querySelectorAll(`.${css.slide}`).forEach((slide, i) => {
			slide.classList.toggle(css.slideActive, i === current);
		});
	}, [current, slides, track]);

	return (
		<div className={css.module} ref={setCanvas}>
			<div className={css.box}>
				<div className={css.square} />
				<div className={css.square} />
				<div className={css.square} />
				<div className={css.boxInner}>
					<div className={css.boxTrack} ref={setTrack}>
						<CareerFeaturesViewIcons {...props} />
					</div>
				</div>
			</div>
		</div>
	);
});

export const CareerFeaturesViewIcons: FC<CareerFeaturesViewProps> = React.memo(({ slides }) => {
	return (
		<>
			{slides.map((slide, i) => (
				<div key={slide.title + '-' + i} className={css.slide}>
					{slide?.icon?.src && (
						<Image
							src={slide.icon.src}
							alt={slide.title}
							width="115"
							height="115"
							layout="intrinsic"
						/>
					)}
				</div>
			))}
		</>
	);
});
